/**
 * useRoles — dinamik rol listesi
 *
 * Backend: GET /admin/roles?only_active=true → [{ id, slug, display_name,
 * hierarchy_level, is_system, is_active, description, ... }, ...]
 *
 * Cache: localStorage'a kaydedilir, mount'ta yeniden fetch edilir.
 * Personel modal/detay sayfalarında ortak kullanılır (PersonnelPage,
 * PersonnelForm, PersonnelDetailsDialog).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import apiClient from "../api/client";
import { isSystemAdminRole } from "../utils/roleUtils";

const STORAGE_KEY = "belvizyon_roles_v1";

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota dolu olabilir — sessiz geç */
  }
}

export function useRoles({ onlyActive = true, enabled = true } = {}) {
  const cached = readCache();
  const [roles, setRoles] = useState(cached || []);
  const [loading, setLoading] = useState(enabled ? !cached : false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = onlyActive ? { only_active: true } : {};
      const res = await apiClient.get("/admin/roles", { params });
      const list = Array.isArray(res.data) ? res.data : [];
      setRoles(list);
      writeCache(list);
      return list;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onlyActive]);

  useEffect(() => {
    // `enabled=false` iken (ör. `/admin/roles` = `yonetim.rol_yonet` yetkisi
    // olmayan rol, ya da kapalı bir personel diyaloğu) istek ATILMAZ — aksi
    // halde backend 403 döner ve tarayıcı console'una hata düşer (Kapı 11).
    if (!enabled) {
      setLoading(false);
      return;
    }
    refresh().catch(() => { /* error state'te zaten */ });
  }, [refresh, enabled]);

  const sortedRoles = useMemo(() => {
    return [...roles].sort((a, b) => {
      const la = Number(a?.hierarchy_level ?? 0);
      const lb = Number(b?.hierarchy_level ?? 0);
      if (la !== lb) return lb - la;
      return String(a?.display_name || a?.slug || "").localeCompare(
        String(b?.display_name || b?.slug || ""),
        "tr"
      );
    });
  }, [roles]);

  const roleById = useMemo(() => {
    const map = new Map();
    for (const r of roles) {
      if (r?.id) map.set(String(r.id), r);
    }
    return map;
  }, [roles]);

  const roleBySlug = useMemo(() => {
    const map = new Map();
    for (const r of roles) {
      if (r?.slug) map.set(String(r.slug), r);
    }
    return map;
  }, [roles]);

  const getRoleById = useCallback((id) => roleById.get(String(id)) || null, [roleById]);
  const getRoleBySlug = useCallback((slug) => roleBySlug.get(String(slug)) || null, [roleBySlug]);
  const getRoleLabel = useCallback(
    (slugOrId) => {
      if (!slugOrId) return "";
      const byId = roleById.get(String(slugOrId));
      if (byId) return byId.display_name || byId.slug || "";
      const bySlug = roleBySlug.get(String(slugOrId));
      if (bySlug) return bySlug.display_name || bySlug.slug || "";
      return String(slugOrId);
    },
    [roleById, roleBySlug]
  );

  // Hiyerarşi/personel listelerinde kullanılacak roller — sistem admini hariç.
  // Admin teknik bir rol, "yönetici" veya "personel" kategorisinde değil.
  const hierarchyRoles = useMemo(
    () => sortedRoles.filter((r) => !isSystemAdminRole(r?.slug)),
    [sortedRoles]
  );

  return {
    roles: sortedRoles,
    hierarchyRoles,
    rolesLoading: loading,
    rolesError: error,
    getRoleById,
    getRoleBySlug,
    getRoleLabel,
    refreshRoles: refresh,
  };
}

export default useRoles;
